import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { CalcWizard } from "@/components/calculator/CalcWizard";
import { ChoiceStep, MultiChoiceStep, InputStep, IconChoiceStep } from "@/components/calculator/fields";
import { Building, Home, GraduationCap, HelpCircle } from "lucide-react";
import { PlateStep } from "@/components/calculator/PlateStep";
import { SirenStep, BonusStep, UploadStep } from "@/components/calculator/specialSteps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YES_NO = [{ value: "non", label: "Non" }, { value: "oui", label: "Oui" }];

const choice = (id, title, options, extra = {}) => ({
  id, ...extra,
  render: ({ answers, next }) => <ChoiceStep title={title} options={options} value={answers[id]} onSelect={(v) => next({ [id]: v })} />,
});
const input = (id, title, props = {}, extra = {}) => ({
  id, ...extra,
  render: ({ answers, next }) => <InputStep title={title} value={answers[id]} onNext={(v) => next({ [id]: v })} {...props} />,
});
const sirenStep = { id: "entreprise", render: (p) => <SirenStep {...p} /> };

export function AutoMotoCalc() {
  const location = useLocation();
  const moto = location.pathname.startsWith("/devis/moto");
  const initialPlate = location.state?.vehicleInfo?.plate || location.state?.plate || "";
  const steps = [
    { id: "vehicule", render: (p) => <PlateStep moto={moto} initialPlate={initialPlate} {...p} /> },
    { id: "bonus", render: (p) => <BonusStep {...p} /> },
    { id: "releve", render: (p) => <UploadStep {...p} /> },
  ];
  return <CalcWizard key={moto ? "moto" : "auto"} title={moto ? "Devis assurance moto" : "Devis assurance auto"} product={moto ? "moto" : "auto"} steps={steps} />;
}

export function RCDecennaleCalc() {
  const [activities, setActivities] = useState([]);
  useEffect(() => {
    axios.get(`${API}/calc/config`).then((r) => setActivities(r.data.rc_activites)).catch(() => {});
  }, []);
  const options = [...activities.map((a) => ({ value: a.id, label: a.label })), { value: "autre", label: "Autre" }];
  const steps = [
    sirenStep,
    { id: "activites", render: ({ answers, set, next }) => (
      <MultiChoiceStep title="Quels sont vos métiers ?" subtitle="Jusqu'à 5 métiers — le tarif se base sur le plus cher"
        options={options} max={5} value={answers.activites || []} onChange={(v) => set({ activites: v })} onNext={() => next()} />
    ) },
    input("chiffre_affaires", "Quel est votre chiffre d'affaires annuel ?", { suffix: "€ HT" }),
    input("experience_annees", "Combien d'années d'expérience dans le métier ?", { suffix: "ans" }),
    choice("sinistres", "Avez-vous eu des sinistres ces 5 dernières années ?", YES_NO),
  ];
  return <CalcWizard title="Devis RC Décennale" product="rc_decennale" steps={steps} />;
}

export function MultirisqueProCalc() {
  const steps = [
    sirenStep,
    input("activite", "Quelle est votre activité ?", { type: "text", placeholder: "Ex : boulangerie, agence, atelier…" }),
    input("surface", "Quelle est la surface de vos locaux ?", { suffix: "m²", min: 1 }),
    input("valeur_materiel", "Quelle est la valeur totale de votre matériel et de votre stock ?", { suffix: "€" }),
  ];
  return <CalcWizard title="Devis Multirisque Professionnelle" product="multirisque_pro" steps={steps} />;
}

export function HabitationCalc() {
  const steps = [
    { id: "type_logement", render: ({ answers, next }) => (
      <IconChoiceStep title="Quel est votre type de logement ?" value={answers.type_logement} onSelect={(v) => next({ type_logement: v })}
        options={[
          { value: "appartement", label: "Appartement", icon: Building },
          { value: "maison", label: "Maison", icon: Home },
          { value: "etudiant", label: "Logement étudiant", icon: GraduationCap },
          { value: "autre", label: "Autre", icon: HelpCircle },
        ]} />
    ) },
    choice("statut", "Vous êtes :", [
      { value: "locataire", label: "Locataire" },
      { value: "proprietaire_occupant", label: "Propriétaire occupant" },
      { value: "proprietaire_non_occupant", label: "Propriétaire non occupant" },
    ], { skip: (a) => a.type_logement === "autre" || a.type_logement === "etudiant" }),
    input("surface", "Quelle est la surface du logement ?", { suffix: "m²", min: 1 }, { skip: (a) => a.type_logement === "autre" }),
    input("code_postal", "Quel est le code postal du logement ?", { type: "text", placeholder: "93200", validate: (v) => /^\d{5}$/.test(v) }),
    choice("formule", "Quelle formule souhaitez-vous ?", [
      { value: "essentielle", label: "Essentielle — RC, incendie, dégâts des eaux" },
      { value: "confort", label: "Confort — + vol, bris de glace" },
      { value: "tous_risques", label: "Tous risques — + valeur à neuf, objets de valeur, assistance" },
    ], { skip: (a) => a.type_logement === "autre" }),
  ];
  return <CalcWizard title="Devis Multirisque Habitation" product="habitation" steps={steps} />;
}

export function CyberCalc() {
  const steps = [sirenStep, input("chiffre_affaires", "Quel est votre chiffre d'affaires annuel ?", { suffix: "€ HT" })];
  return <CalcWizard title="Devis Cyber-sécurité" product="cybersecurite" steps={steps} />;
}
